import React, {useMemo, useState} from 'react';
import {Link, usePage} from '@inertiajs/react';
import {Badge, Card, Container, Form, Table} from 'react-bootstrap';
import {b2cProductUrl} from '../../functions/shopUrls.js';
import {compareForSort} from '../../functions/sorting.js';
import SortableTh from '../../Components/SortableTh.jsx';

const STATUS_MAP = {
    pending: {label: 'Wartet', variant: 'secondary'},
    processing: {label: 'Läuft', variant: 'info'},
    completed: {label: 'Fertig', variant: 'success'},
    failed: {label: 'Fehler', variant: 'danger'},
};

const TRIGGER_MAP = {
    schedule: 'Automatisch (Zeitplan)',
    manual: 'Manuell',
    'dry-run': 'Testlauf (Dry-Run)',
};

function formatTimestamp(value) {
    if (!value) {
        return '–';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '–';
    }
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    });
}

function formatDuration(ms) {
    if (ms === null || ms === undefined) {
        return '–';
    }
    if (ms < 1000) {
        return `${ms} ms`;
    }
    return `${(ms / 1000).toFixed(1)} s`;
}

function DiffBadge({from, to}) {
    const fromNum = Number(from);
    const toNum = Number(to);
    if (!Number.isFinite(fromNum) || !Number.isFinite(toNum)) {
        return <span className="text-muted">–</span>;
    }
    const diff = toNum - fromNum;
    const variant = diff > 0 ? 'success' : diff < 0 ? 'danger' : 'secondary';
    const sign = diff > 0 ? '+' : '';
    return <Badge bg={variant}>{`${sign}${diff}`}</Badge>;
}

// Spalten der Änderungsliste inkl. Typ (für die Sortierung) und Zugriff auf
// den zu vergleichenden Wert - "Differenz" wird aus den beiden Mengen
// abgeleitet statt aus dem Change-Objekt gelesen (es gibt kein eigenes Feld).
const CHANGE_COLUMNS = [
    {key: 'sku', label: 'Artikelnummer', type: 'text', getValue: (change) => change.sku},
    {key: 'from_qty', label: 'Alte Menge', type: 'number', getValue: (change) => change.from_qty},
    {key: 'to_qty', label: 'Neue Menge', type: 'number', getValue: (change) => change.to_qty},
    {
        key: 'diff',
        label: 'Differenz',
        type: 'number',
        getValue: (change) => {
            const fromNum = Number(change.from_qty);
            const toNum = Number(change.to_qty);
            return Number.isFinite(fromNum) && Number.isFinite(toNum) ? toNum - fromNum : null;
        },
    },
];

export default function B2cRunDetail() {
    const {run, changes, product_ids: productIds} = usePage().props;
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    const handleSort = (key) => {
        if (key === sortKey) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const filteredChanges = useMemo(() => {
        const list = Array.isArray(changes) ? changes : [];
        const term = search.trim().toLowerCase();
        if (!term) {
            return list;
        }
        return list.filter((change) => String(change.sku ?? '').toLowerCase().includes(term));
    }, [changes, search]);

    const sortedChanges = useMemo(() => {
        if (!sortKey) {
            return filteredChanges;
        }
        const column = CHANGE_COLUMNS.find((c) => c.key === sortKey);
        if (!column) {
            return filteredChanges;
        }
        const factor = sortDir === 'asc' ? 1 : -1;
        return [...filteredChanges].sort(
            (a, b) => factor * compareForSort(column.getValue(a), column.getValue(b), column.type)
        );
    }, [filteredChanges, sortKey, sortDir]);

    const statusCfg = STATUS_MAP[run.status] || {label: run.status, variant: 'light'};

    return (
        <Container fluid className="pt-2">
            <div className="mt-1 mb-3 d-flex align-items-center gap-3">
                <h1 className="mb-0">Lauf-Details – B2C-Bestandsaktualisierung</h1>
            </div>

            <div className="mb-3">
                <Link href="/stock/b2c-availability" className="btn btn-outline-secondary btn-sm">
                    ← Zurück zum Log
                </Link>
            </div>

            <Card className="mb-4">
                <Card.Body>
                    <div className="row row-cols-2 row-cols-md-6 g-3">
                        <div>
                            <div className="text-muted small">Zeitpunkt</div>
                            <div>{formatTimestamp(run.started_at)}</div>
                        </div>
                        <div>
                            <div className="text-muted small">Auslöser</div>
                            <div>{TRIGGER_MAP[run.trigger] || run.trigger || '–'}</div>
                        </div>
                        <div>
                            <div className="text-muted small">Status</div>
                            <div><Badge bg={statusCfg.variant}>{statusCfg.label}</Badge></div>
                        </div>
                        <div>
                            <div className="text-muted small">Geprüft</div>
                            <div>{run.articles_checked ?? '–'}</div>
                        </div>
                        <div>
                            <div className="text-muted small">Änderungen</div>
                            <div>{run.changes_count ?? '–'}</div>
                        </div>
                        <div>
                            <div className="text-muted small">Dauer</div>
                            <div>{formatDuration(run.duration_ms)}</div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Card.Title className="mb-0">Geänderte Artikel</Card.Title>
                        <Form.Control
                            type="search"
                            placeholder="Suche nach Artikelnummer…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{maxWidth: 320}}
                        />
                    </div>

                    {filteredChanges.length === 0 ? (
                        <p className="text-muted mb-0">
                            {Array.isArray(changes) && changes.length > 0
                                ? 'Keine Artikel passen zur Suche.'
                                : 'Dieser Lauf hat keine Änderungen vorgenommen.'}
                        </p>
                    ) : (
                        <div style={{maxHeight: '70vh', overflowY: 'auto'}}>
                            <Table striped hover size="sm" className="mb-0">
                                <thead>
                                    <tr>
                                        {CHANGE_COLUMNS.map((column) => (
                                            <SortableTh
                                                key={column.key}
                                                column={column}
                                                sortKey={sortKey}
                                                sortDir={sortDir}
                                                onSort={handleSort}
                                            />
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedChanges.map((change, index) => (
                                        <tr key={`${change.sku ?? 'row'}-${index}`}>
                                            <td className="text-nowrap">
                                                {change.sku ? (
                                                    <a
                                                        href={b2cProductUrl(change.sku, productIds?.[change.sku])}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Im B2C-Shop öffnen (shop.molotow.com)"
                                                    >
                                                        {change.sku}
                                                    </a>
                                                ) : '–'}
                                            </td>
                                            <td>{change.from_qty ?? '–'}</td>
                                            <td>{change.to_qty ?? '–'}</td>
                                            <td><DiffBadge from={change.from_qty} to={change.to_qty}/></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    <div className="text-muted small mt-2">
                        {filteredChanges.length} von {Array.isArray(changes) ? changes.length : 0} Änderungen angezeigt.
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
}
